'use client'

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { isCheckoutInterrupted } from '@/features/veluvit/lib/payment-status'
import {
  clearPurchaseAttempt,
  readPaymentOrder,
  readPurchaseAttempt,
  refreshPaymentWallet,
  syncPaymentOrder,
  type PaymentOrder,
} from '@/features/veluvit/services/payment.service'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

const POLL_INTERVAL = 5000
const POLL_LIMIT = 12
const CHECK_COOLDOWN = 7000
const CLOSED_REFUNDS = new Set([
  'refunded',
  'succeeded',
  'complete',
  'completed',
  'failed',
  'rejected',
  'cancelled',
])
const TERMINAL_ORDERS = new Set([
  'paid',
  'partially_refunded',
  'refunded',
  'failed',
])

function shouldWatch(order: PaymentOrder | null, watchInterrupted: boolean) {
  if (!order) return false
  return (
    (order.status === 'pending' &&
      (watchInterrupted || !isCheckoutInterrupted(order))) ||
    order.status === 'awaiting_deposit' ||
    order.refunds?.some(
      (refund) => !CLOSED_REFUNDS.has(refund.status.toLowerCase())
    ) === true
  )
}

export function usePaymentOrder(
  ownerId: string,
  orderId: string,
  {
    autoSync = true,
    pollLimit = POLL_LIMIT,
    watchInterrupted = false,
  }: { autoSync?: boolean; pollLimit?: number; watchInterrupted?: boolean } = {}
) {
  const [order, setOrder] = useState<PaymentOrder | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [walletError, setWalletError] = useState<string | null>(null)
  const [walletSettled, setWalletSettled] = useState(false)
  const [polling, setPolling] = useState(false)
  const scopeRef = useRef<{
    ownerId: string
    orderId: string
    identity: number
    epoch: number
  } | null>(null)
  const epochRef = useRef(0)
  const orderRef = useRef<PaymentOrder | null>(null)
  const operationRef = useRef<Promise<void> | null>(null)
  const walletRef = useRef<Promise<void> | null>(null)
  const controllersRef = useRef(new Set<AbortController>())
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const countRef = useRef(0)
  const lastCheckRef = useRef(0)
  const walletSeenRef = useRef(new Set<string>())
  const scheduleRef = useRef<() => void>(() => {})
  const stateKey = JSON.stringify([
    ownerId,
    orderId,
    autoSync,
    pollLimit,
    watchInterrupted,
  ])
  const [seenStateKey, setSeenStateKey] = useState(stateKey)
  // 주문·계정이 바뀐 첫 렌더부터 이전 주문을 보이지 않는다
  if (seenStateKey !== stateKey) {
    setSeenStateKey(stateKey)
    setOrder(null)
    setError(null)
    setWalletError(null)
    setWalletSettled(false)
    setPolling(false)
  }
  // 커밋과 함께 진행 중인 이전 응답을 끊는다 — passive effect까지 기다리면 그 사이 도착한 응답이 새 렌더에 남는다
  useLayoutEffect(
    () => () => {
      scopeRef.current = null
    },
    [stateKey]
  )

  const active = useCallback(
    (epoch?: number) => {
      const scope = scopeRef.current
      const state = useSessionStore.getState()
      return (
        scope?.ownerId === ownerId &&
        scope.orderId === orderId &&
        (epoch === undefined || scope.epoch === epoch) &&
        scope.identity === state.identity &&
        state.data?.user?.id === ownerId &&
        state.data?.user?.guest === false
      )
    },
    [ownerId, orderId]
  )

  const applyOrder = useCallback(
    (next: PaymentOrder, epoch?: number) => {
      if (!active(epoch)) return
      const previous = orderRef.current
      const refundState = (value: PaymentOrder | null) =>
        value?.refunds
          ?.map((refund) => `${refund.id}:${refund.status}`)
          .join('|') ?? ''
      if (previous && refundState(previous) !== refundState(next))
        countRef.current = 0
      orderRef.current = next
      setOrder(next)
      if (TERMINAL_ORDERS.has(next.status)) {
        const attempt = readPurchaseAttempt(ownerId)
        if (attempt?.orderId === next.id) clearPurchaseAttempt(ownerId)
      }
    },
    [active, ownerId]
  )

  const readNow = useCallback(async () => {
    const epoch = scopeRef.current?.epoch
    if (!active(epoch)) return
    const controller = new AbortController()
    controllersRef.current.add(controller)
    try {
      const next = await readPaymentOrder(ownerId, orderId, controller.signal)
      applyOrder(next, epoch)
    } finally {
      controllersRef.current.delete(controller)
    }
  }, [active, applyOrder, ownerId, orderId])

  const refresh = useCallback((): Promise<void> => {
    if (!active()) return Promise.resolve()
    if (operationRef.current) return operationRef.current
    const epoch = scopeRef.current?.epoch
    setBusy(true)
    setError(null)
    const flight = readNow()
      .catch((cause: unknown) => {
        if (active(epoch))
          setError(
            cause instanceof Error
              ? cause.message
              : '주문 상태를 확인하지 못했어요.'
          )
      })
      .finally(() => {
        if (operationRef.current === flight) operationRef.current = null
        if (active(epoch)) {
          setBusy(false)
          scheduleRef.current()
        }
      })
    operationRef.current = flight
    return flight
  }, [active, readNow])

  const check = useCallback((): Promise<void> => {
    if (!active()) return Promise.resolve()
    if (orderRef.current?.provider !== 'toss') return refresh()
    if (operationRef.current) return operationRef.current
    if (Date.now() - lastCheckRef.current < CHECK_COOLDOWN) {
      setError('잠시 후 다시 확인해 주세요.')
      return Promise.resolve()
    }
    lastCheckRef.current = Date.now()
    const epoch = scopeRef.current?.epoch
    setBusy(true)
    setError(null)
    const flight = (async () => {
      const synced = await syncPaymentOrder(ownerId, orderId)
      if (!active(epoch)) return
      applyOrder(
        synced.refunds === undefined && orderRef.current?.id === synced.id
          ? { ...synced, refunds: orderRef.current.refunds }
          : synced,
        epoch
      )
      await readNow()
    })()
      .catch((cause: unknown) => {
        if (active(epoch))
          setError(
            cause instanceof Error
              ? cause.message
              : '주문 상태를 확인하지 못했어요.'
          )
      })
      .finally(() => {
        if (operationRef.current === flight) operationRef.current = null
        if (active(epoch)) {
          setBusy(false)
          countRef.current = 0
          scheduleRef.current()
        }
      })
    operationRef.current = flight
    return flight
  }, [active, applyOrder, ownerId, orderId, readNow, refresh])

  const refreshWallet = useCallback((): Promise<void> => {
    if (!active()) return Promise.resolve()
    if (walletRef.current) return walletRef.current
    const epoch = scopeRef.current?.epoch
    setWalletError(null)
    setWalletSettled(false)
    const flight = refreshPaymentWallet(ownerId)
      .then(() => {
        if (active(epoch)) setWalletError(null)
      })
      .catch((cause: unknown) => {
        if (active(epoch))
          setWalletError(
            cause instanceof Error
              ? cause.message
              : '지갑을 다시 불러오지 못했어요.'
          )
      })
      .finally(() => {
        if (walletRef.current === flight) walletRef.current = null
        if (active(epoch)) setWalletSettled(true)
      })
    walletRef.current = flight
    return flight
  }, [active, ownerId])

  useEffect(() => {
    const identity = useSessionStore.getState().identity
    const epoch = ++epochRef.current
    const controllers = controllersRef.current
    scopeRef.current = { ownerId, orderId, identity, epoch }
    orderRef.current = null
    operationRef.current = null
    walletRef.current = null
    countRef.current = 0
    lastCheckRef.current = 0
    walletSeenRef.current.clear()
    let stopped = false
    const schedule = () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = null
      const watching =
        !stopped &&
        active() &&
        shouldWatch(orderRef.current, watchInterrupted) &&
        countRef.current < pollLimit
      setPolling(watching)
      if (!watching) return
      timerRef.current = setTimeout(async () => {
        timerRef.current = null
        if (stopped || !active()) return
        if (operationRef.current) {
          schedule()
          return
        }
        countRef.current += 1
        await refresh()
        if (!stopped) schedule()
      }, POLL_INTERVAL)
    }
    scheduleRef.current = schedule
    const unsubscribe = useSessionStore.subscribe((state) => {
      if (
        state.identity === identity &&
        state.data?.user?.id === ownerId &&
        state.data?.user?.guest === false
      )
        return
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = null
      for (const controller of controllers) controller.abort()
      setPolling(false)
    })
    const begin = async () => {
      await refresh()
      if (stopped || !active()) return
      if (
        autoSync &&
        orderRef.current?.provider === 'toss' &&
        orderRef.current.status === 'pending' &&
        !isCheckoutInterrupted(orderRef.current)
      )
        await check()
      if (!stopped) schedule()
    }
    void begin()
    return () => {
      stopped = true
      unsubscribe()
      if (scopeRef.current?.epoch === epoch) scopeRef.current = null
      scheduleRef.current = () => {}
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = null
      for (const controller of controllers) controller.abort()
      controllers.clear()
    }
  }, [
    active,
    autoSync,
    check,
    orderId,
    ownerId,
    pollLimit,
    refresh,
    watchInterrupted,
  ])

  useEffect(() => {
    if (
      !order ||
      !TERMINAL_ORDERS.has(order.status) ||
      order.status === 'failed' ||
      !active()
    )
      return
    const key = `${order.id}:${order.status}:${order.refundedAmount}`
    if (walletSeenRef.current.has(key)) return
    walletSeenRef.current.add(key)
    void refreshWallet()
  }, [active, order, refreshWallet])

  return {
    order,
    busy,
    error,
    walletError,
    walletSettled,
    polling,
    refresh,
    check,
    refreshWallet,
  }
}
